import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, TextField, Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress, Divider, Checkbox, FormGroup, FormControlLabel, Snackbar, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Person from '@mui/icons-material/Person';
import PersonAdd from '@mui/icons-material/PersonAdd';
import Business from '@mui/icons-material/Business';
import Email from '@mui/icons-material/Email';
import Phone from '@mui/icons-material/Phone';
import LocationOn from '@mui/icons-material/LocationOn';
import Public from '@mui/icons-material/Public';
import Comment from '@mui/icons-material/Comment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import Campaign from '@mui/icons-material/Campaign';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../../services/api';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

import logger from '../../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));


const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  maxWidth: '900px',
  margin: '0 auto',
  backgroundColor: '#fff',
}));

/* Removed several unused styled helpers and duplicate/unused icon imports to satisfy ESLint */

const AddLead = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [employees, setEmployees] = useState([]);

  // Get employee ID for auto-assignment when BDM adds a lead
  const { employeeId, isSalesTeam, employeeData } = useAdminRole();

  const [formState, setFormState] = useState({
    contact_person: '',
    business_name: '',
    email: '',

    phone_number: '',
    alt_number: '',

    is_gmb_services: false,
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    comment: '',
    lead_source: '',
    assigned_employee_id: '' // For manual assignment
  });

  // Fetch employees on mount if admin, or set self if sales team
  useEffect(() => {
    if (isSalesTeam && employeeId) {
      setFormState(prev => ({ ...prev, assigned_employee_id: employeeId }));
    } else {
      // If admin, fetch employees for dropdown
      const fetchEmployees = async () => {
        try {
          // Fetching a large page to simulate "All Employees" for the dropdown
          const response = await ApiService.get('/admin/employee/get-employees?page=1&pageSize=100');
          if (response?.data?.data?.employees) {
            setEmployees(response.data.data.employees);
          } else if (response?.data?.employees) {
            setEmployees(response.data.employees);
          }
        } catch (err) {
          logger.error('Error fetching employees for dropdown:', err);
        }
      };
      fetchEmployees();
    }
  }, [isSalesTeam, employeeId]);


  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'tags') {
      const tagsArray = value.split(',').map(tag => tag.trim()).filter(tag => tag);
      setFormState(prev => ({ ...prev, tags: tagsArray.join(', ') }));
    } else {
      setFormState(prev => ({ ...prev, [name]: value }));
    }
  };

  // removed unused helper for splitting person name (not used in form)

  // Handle GMB checkbox
  const handleGmbCheckbox = (e) => {
    setFormState(prev => ({ ...prev, is_gmb_services: e.target.checked }));
  };

  // Fetch location details from pincode using India Post API
  const fetchLocationFromPincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;

    setPincodeLoading(true);
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();

      if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setFormState(prev => ({
          ...prev,
          city: postOffice.District || postOffice.Division || '',
          state: postOffice.State || '',
          country: postOffice.Country || 'India',
        }));
      }
    } catch (err) {
      logger.error('Error fetching pincode details:', err);
    } finally {
      setPincodeLoading(false);
    }
  };

  // Handle pincode change with auto-fetch
  const handlePincodeChange = (e) => {
    const pincode = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    setFormState(prev => ({ ...prev, zip_code: pincode }));

    if (pincode.length === 6) {
      fetchLocationFromPincode(pincode);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Determine final assigned employee
      // If sales team, force their own ID. If admin, use selected ID from dropdown.
      const finalEmployeeId = isSalesTeam && employeeId ? employeeId : (formState.assigned_employee_id || null);

      const payload = {
        contact_person: formState.contact_person,
        business_name: formState.business_name,
        email: formState.email,

        phone_number: formState.phone_number,
        alt_number: formState.alt_number,

        address_line1: formState.address_line1,
        city: formState.city,
        state: formState.state,
        zip_code: formState.zip_code,
        country: formState.country,
        comment: formState.comment,
        lead_source: formState.lead_source,
        is_gmb_services: formState.is_gmb_services,
        is_website_service: false,
        is_smo_services: false,
        is_other_services: false,
        employee_id: finalEmployeeId,
      };
      const response = await ApiService.post('/admin/leads/add-lead', payload);
      if (response) {
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate('/admin-dashboard/leads');
        }, 1500);
        return;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      logger.error('Error adding lead:', err);
      setError(err.response?.data?.message || err.message || 'Failed to add lead. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {/* Beautiful Header with Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
          }}
        >
          <PersonAdd sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>Add New Lead</Typography>
          <Typography variant="body2" color="text.secondary">
            Add a new lead to help your team track prospects and follow up effectively.
          </Typography>
        </Box>
      </Box>

      <FormContainer>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#1e3a8a' }}>
            Lead Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Person & Business */}
            <TextField
              fullWidth
              size="small"
              label="Contact Person"
              name="contact_person"
              value={formState.contact_person}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <Person sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Business Name"
              name="business_name"
              value={formState.business_name}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <Business sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />

            {/* Email & Phone */}
            <TextField
              fullWidth
              size="small"
              label="Email Address"
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <Email sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Contact Number"
              name="phone_number"
              value={formState.phone_number}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <Phone sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Alternative Contact"
              name="alt_number"
              value={formState.alt_number}
              onChange={handleChange}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <Phone sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />

            <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}>
              <InputLabel>Lead Source</InputLabel>
              <Select
                name="lead_source"
                value={formState.lead_source}
                label="Lead Source"
                onChange={handleChange}
                startAdornment={<Campaign sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />}
              >
                <MenuItem value="Cold Call">Cold Call</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Assign BDM Section */}
            {isSalesTeam ? (
              <TextField
                fullWidth
                size="small"
                label="Assigned BDM"
                value={`Self (${employeeData?.name || 'Me'})`}
                disabled
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#1e3a8a', fontWeight: 600 },
                  '& .MuiOutlinedInput-root.Mui-disabled fieldset': { borderColor: '#93c5fd' }
                }}
                InputProps={{
                  startAdornment: <AssignmentIndIcon sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
                }}
              />
            ) : (
              <FormControl fullWidth size="small" sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}>
                <InputLabel>Assign BDM</InputLabel>
                <Select
                  name="assigned_employee_id"
                  value={formState.assigned_employee_id}
                  label="Assign BDM"
                  onChange={handleChange}
                  startAdornment={<AssignmentIndIcon sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id || emp._id} value={emp.id || emp._id}>
                      {emp.first_name} {emp.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Address Section */}
            <Typography variant="subtitle2" sx={{ mt: 1, color: '#1e40af', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn fontSize="small" /> Location Details
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Address Line 1"
              name="address_line1"
              value={formState.address_line1}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <LocationOn sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />

            {/* Grouping small address fields for better UX even in vertical layout context */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Pincode"
                name="zip_code"
                value={formState.zip_code}
                onChange={handlePincodeChange}
                required
                placeholder="6 digits"
                sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
                InputProps={{
                  endAdornment: pincodeLoading ? <CircularProgress size={16} /> : null,
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="City"
                name="city"
                value={formState.city}
                onChange={handleChange}
                required
                disabled={pincodeLoading}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="State"
                name="state"
                value={formState.state}
                onChange={handleChange}
                required
                disabled={pincodeLoading}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Country"
                name="country"
                value={formState.country}
                onChange={handleChange}
                disabled={pincodeLoading}
                sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              />
            </Box>

            {/* Services & Comment */}
            <Box>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formState.is_gmb_services}
                      onChange={handleGmbCheckbox}
                      sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#2563eb' } }}
                    />
                  }
                  label={<Typography fontSize="14px" color="#1e3a8a" fontWeight={500}>Google My Business</Typography>}
                />
              </FormGroup>
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Comment"
              name="comment"
              placeholder="Add any additional notes..."
              value={formState.comment}
              onChange={handleChange}
              sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#2563eb' }, '& .MuiInputLabel-root.Mui-focused': { color: '#2563eb' } }}
              InputProps={{
                startAdornment: <Comment sx={{ color: '#3b82f6', mr: 1.5, fontSize: 20 }} />,
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={loading}
              sx={{ color: '#64748b', borderColor: '#e2e8f0', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#cbd5e1', background: '#f8fafc' } }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
              sx={{
                borderRadius: '8px',
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  boxShadow: '0 6px 16px rgba(37, 99, 235, 0.3)',
                },
              }}
            >
              {loading ? 'Adding...' : 'Save Lead'}
            </Button>
          </Box>
        </form>
      </FormContainer>

      {/* Success Toast Notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: '#10B981',
            color: 'white',
            px: 2.5,
            py: 1.5,
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
            minWidth: '280px',
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 24 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Lead Added Successfully!</Typography>
            <Typography sx={{ fontSize: '12px', opacity: 0.9 }}>Redirecting to dashboard...</Typography>
          </Box>
        </Box>
      </Snackbar>
    </Container>
  );
};

export default AddLead;
